import posthog from 'posthog-js';
import { onScopeDispose, ref, type Ref } from 'vue';

// ponytail: 3s ceiling, same as PostHog's own flag request.
// A later answer is ignored so the step list stays put.
const FLAG_SNAPSHOT_TIMEOUT_MS = 3000;

/**
 * Frozen flag for handover and return, so a step cannot appear or disappear mid-process.
 * Waits for PostHog's first `onFeatureFlags` call, which also runs when loading failed.
 * If that callback never runs, gives up after a few seconds and keeps the value PostHog
 * already has — off, when PostHog itself never started.
 */
export const useFeatureFlagSnapshot = (flag: string) => {
  const isEnabled = ref(false);
  const isLoaded = ref(!posthog.__loaded);
  const timeout = setTimeout(() => {
    if (isLoaded.value) return;
    isEnabled.value = !!posthog.isFeatureEnabled(flag);
    isLoaded.value = true;
  }, FLAG_SNAPSHOT_TIMEOUT_MS);
  const unsubscribe = posthog.onFeatureFlags((flags) => {
    if (isLoaded.value) return;
    clearTimeout(timeout);
    isEnabled.value = flags.includes(flag);
    isLoaded.value = true;
  });
  onScopeDispose(() => {
    clearTimeout(timeout);
    unsubscribe();
  });
  return { isEnabled, isLoaded };
};

export const useFeatureFlag = (flag: string): Ref<boolean | undefined> => {
  const value = ref(posthog.isFeatureEnabled(flag));
  posthog.onFeatureFlags((flags) => {
    value.value = flags.includes(flag);
  });
  return value;
};
